import React, { useEffect, useState } from "react";
import { HiUsers, HiUserAdd, HiOutlineClock, HiCheck, HiXCircle } from "react-icons/hi";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import LeadStatCard from "../../ui/LeadStatCard";
import {
  isTruthy,
  openErrorNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import { getEnquiry } from "./Service";

/** react-icons + React 19 type bridge */
type IconComp = React.ComponentType<{ size?: number; className?: string }>;
const UsersIcon = HiUsers as IconComp;
const UserAddIcon = HiUserAdd as IconComp;
const ClockIcon = HiOutlineClock as IconComp;
const CheckIconRi = HiCheck as IconComp;
const XCircleIcon = HiXCircle as IconComp;

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ServiceLeadList = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalRows, setTotalRows] = useState(0);

  // Route access already gates this page; don't hide the table when privilege
  // keys are missing/mismatched (same pattern as My Leads).
  const canView = true;

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    handleTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, search, activeOrganizationId]);

  const handleTableData = async () => {
    try {
      setLoading(true);
      const response = await getEnquiry({
        pageIndex: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search,
      });
      setTableData(
        [...(response?.data || [])].sort((a: any, b: any) => {
          const tb = new Date(b?.createdAt || 0).getTime() || 0;
          const ta = new Date(a?.createdAt || 0).getTime() || 0;
          return tb - ta;
        })
      );
      setTotalRows(response?.total || response?.data?.length || 0);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const serviceTypeCounts = tableData.reduce(
    (acc: Record<string, number>, row: any) => {
      const key = String(row?.serviceType || "Other").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    {}
  );

  const columns: YhTableColumn[] = [
    {
      headerName: "Lead",
      field: "name",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.name || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" },
          { bg: "#DBEAFE", color: "#2563EB" },
          { bg: "#D1FAE5", color: "#059669" },
          { bg: "#FFEDD5", color: "#EA580C" },
          { bg: "#FEF3C7", color: "#D97706" },
          { bg: "#FCE7F3", color: "#DB2777" },
          { bg: "#CCFBF1", color: "#0D9488" },
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="min-w-0 flex flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#111827]">
                {name}
              </span>
              <span className="truncate text-xs text-[#9CA3AF]">
                {params.row?.email || "—"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Contact",
      field: "phoneNumber",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.phoneNumber || "—"}
        </span>
      ),
    },
    {
      headerName: "Service Type",
      field: "serviceType",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="inline-flex rounded-full bg-[#F9F0F4] px-2.5 py-1 text-xs font-semibold capitalize text-[#A02448]">
          {params.row?.serviceType || "—"}
        </span>
      ),
    },
    {
      headerName: "Interested Service",
      field: "service",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-sm capitalize text-[#374151]">
          {params.row?.service || "—"}
        </span>
      ),
    },
    {
      headerName: "Message",
      field: "message",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="line-clamp-2 text-sm text-[#6B7280]">
          {params.row?.message || "—"}
        </span>
      ),
    },
  ];

  const mainContent = (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4 shrink-0">
        <div>
          <h3 className="text-xl font-bold tracking-tight text-[#111827]">
            Service Leads
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            View &amp; manage service enquiries your company is receiving.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[440px] w-full sm:w-[320px]">
            <YhInput
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              placeholder="Search by name, email or phone"
              startAdornment={<SearchSvg />}
              containerClassName="w-full"
            />
          </div>
        </div>
      </div>

      <div className="mb-4 grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-5">
        <LeadStatCard
          title="Total Leads"
          value={totalRows}
          accent="purple"
          icon={<UsersIcon size={20} />}
          loading={loading}
        />
        <LeadStatCard
          title="On Page"
          value={tableData.length}
          accent="blue"
          icon={<UserAddIcon size={20} />}
          loading={loading}
        />
        <LeadStatCard
          title="Service Types"
          value={Object.keys(serviceTypeCounts).length}
          accent="orange"
          icon={<ClockIcon size={20} />}
          loading={loading}
        />
        <LeadStatCard
          title="With Message"
          value={tableData.filter((r) => r?.message).length}
          accent="green"
          icon={<CheckIconRi size={20} />}
          loading={loading}
        />
        <LeadStatCard
          title="Missing Phone"
          value={tableData.filter((r) => !r?.phoneNumber).length}
          accent="red"
          icon={<XCircleIcon size={20} />}
          loading={loading}
        />
      </div>



      <div className="relative min-h-0 flex-1">
        {canView ? (
          <YhTable
            columns={columns}
            rows={tableData}
            getRowId={(row) => row._id || row.id || row.email}
            rowHeight={80}
            loading={loading}
            emptyMessage="No data found"
            className="h-full min-h-[280px]"
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            totalRows={totalRows}
            onPageChange={(p) =>
              setPaginationModel((prev) => ({ ...prev, page: p }))
            }
            onPageSizeChange={(s) =>
              setPaginationModel({ pageSize: s, page: 0 })
            }
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      {mainContent}
    </div>
  );
};

export default ServiceLeadList;
